/**
 * /kitchen — barista order board.
 *
 * - Fetches active orders on mount via GET /api/v1/orders
 * - Joins the 'kitchen' Socket.io room and listens for `order:new` and
 *   `order:status_change` events; updates local state without a refetch
 * - Plays a short beep on new orders (after the user unmutes — browsers
 *   require user interaction before AudioContext can produce sound)
 * - The useTicker keeps elapsed-time readouts current to the second
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Order, OrderStatus } from '@cracked-mug/shared';
import { Volume2, VolumeX } from 'lucide-react';
import { KitchenBoard } from '../components/kitchen/KitchenBoard';
import { StatsBar } from '../components/kitchen/StatsBar';
import { fetchAllOrders, updateOrderStatus } from '../services/ordersApi';
import { fetchSettings, setShopOpen } from '../services/settingsApi';
import { getSocket } from '../services/socket';
import { useTicker } from '../hooks/useTicker';
import { cn } from '../lib/cn';
import { pushToast } from '../store/toastStore';

export function Kitchen() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(false);
  const soundOnRef = useRef(false);
  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  // A single AudioContext, created + resumed during a user tap (iOS keeps a
  // context created in a background socket callback suspended and silent), then
  // reused for every chime. Closed on unmount.
  const audioCtxRef = useRef<AudioContext | null>(null);
  useEffect(() => {
    return () => {
      void audioCtxRef.current?.close();
    };
  }, []);

  const playChime = useCallback((): void => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') void ctx.resume();
      const start = ctx.currentTime;
      // Two-note rising chime — friendlier and more noticeable than a flat beep.
      const tones = [
        { freq: 784, at: 0 },
        { freq: 1175, at: 0.13 },
      ];
      for (const { freq, at } of tones) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const t = start + at;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.22, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        osc.start(t);
        osc.stop(t + 0.37);
      }
    } catch {
      /* audio unavailable */
    }
  }, []);

  // The socket handler reads the chime through a ref so it never re-subscribes.
  const playChimeRef = useRef(playChime);
  useEffect(() => {
    playChimeRef.current = playChime;
  }, [playChime]);

  // Enabling MUST run inside this tap so iOS unlocks audio: create + resume the
  // context, then play a confirmation chime so staff hear it's working.
  const handleToggleSound = useCallback((): void => {
    if (soundOn) {
      setSoundOn(false);
      return;
    }
    setSoundOn(true);
    if (typeof window === 'undefined') return;
    const AudioCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    try {
      audioCtxRef.current ??= new AudioCtor();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        void ctx.resume().then(() => playChime());
      } else {
        playChime();
      }
    } catch {
      /* audio unavailable */
    }
  }, [soundOn, playChime]);

  // Shop open/closed toggle — staff-facing, so it sits up here in the
  // kitchen header instead of buried in admin settings.
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [togglingOpen, setTogglingOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((s) => {
        if (cancelled) return;
        setIsOpen(s.isOpen);
      })
      .catch(() => {
        // Surface failures through the toggle button (it'll show
        // "Couldn't load") rather than blocking the whole kitchen on it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleOpen = useCallback(async (): Promise<void> => {
    if (isOpen === null || togglingOpen) return;
    const next = !isOpen;
    setTogglingOpen(true);
    try {
      const saved = await setShopOpen(next);
      setIsOpen(saved.isOpen);
      pushToast({
        variant: 'success',
        title: saved.isOpen ? 'Shop is open' : 'Shop is closed',
        description: saved.isOpen
          ? 'Teachers can place orders.'
          : 'New orders are blocked until you reopen.',
      });
    } catch (err) {
      pushToast({
        variant: 'error',
        title: "Couldn't update shop status",
        description: err instanceof Error ? err.message : 'Try again.',
      });
    } finally {
      setTogglingOpen(false);
    }
  }, [isOpen, togglingOpen]);

  // Tick once per second to refresh elapsed-time labels.
  useTicker(1000);

  useEffect(() => {
    let cancelled = false;
    fetchAllOrders({ limit: 100 })
      .then((data) => {
        if (cancelled) return;
        setOrders(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const socket = getSocket();
    socket.emit('kitchen:join');

    const handleNew = (order: Order): void => {
      setOrders((prev) => {
        if (prev.some((o) => o.orderId === order.orderId)) return prev;
        return [order, ...prev];
      });
      if (soundOnRef.current) playChimeRef.current();
    };

    const handleStatusChange = (payload: {
      orderId: string;
      newStatus: OrderStatus;
      updatedAt: string;
    }): void => {
      setOrders((prev) =>
        prev.map((o) => (o.orderId === payload.orderId ? { ...o, status: payload.newStatus } : o)),
      );
    };

    const handlePrintConfirm = (payload: { orderId: string; printedAt: string }): void => {
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === payload.orderId ? { ...o, printedAt: payload.printedAt } : o,
        ),
      );
    };

    socket.on('order:new', handleNew);
    socket.on('order:status_change', handleStatusChange);
    socket.on('order:print_confirm', handlePrintConfirm);

    return () => {
      socket.off('order:new', handleNew);
      socket.off('order:status_change', handleStatusChange);
      socket.off('order:print_confirm', handlePrintConfirm);
    };
  }, []);

  const handleAdvance = useCallback(async (order: Order, next: OrderStatus): Promise<void> => {
    try {
      const updated = await updateOrderStatus(order.orderId, next);
      setOrders((prev) => prev.map((o) => (o.orderId === updated.orderId ? updated : o)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  }, []);

  const handleCancel = useCallback(async (order: Order): Promise<void> => {
    try {
      // Cancel via the status-transition endpoint (barista + admin), not the
      // admin-only DELETE route — the student barista running the kitchen must
      // be able to cancel an order without an admin account.
      const updated = await updateOrderStatus(order.orderId, 'cancelled');
      setOrders((prev) => prev.map((o) => (o.orderId === updated.orderId ? updated : o)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel order');
    }
  }, []);

  const visibleOrders = useMemo(() => {
    // Show all active orders + recently completed in the last hour.
    const cutoff = Date.now() - 60 * 60 * 1000;
    return orders.filter((o) => {
      if (o.status === 'cancelled') return false;
      if (o.status === 'completed') {
        return o.completedAt !== null && new Date(o.completedAt).getTime() > cutoff;
      }
      return true;
    });
  }, [orders]);

  return (
    <div className="min-h-dvh">
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_85%_at_88%_0%,rgba(196,18,48,0.06),transparent_62%)]" />
        <div className="relative flex items-center justify-between gap-3 px-6 pt-5">
          <div>
            <h1 className="font-display text-3xl leading-none text-wca-black">Kitchen</h1>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-wca-gray-mid">
              Live order board
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleToggleOpen()}
              disabled={isOpen === null || togglingOpen}
              aria-pressed={isOpen === true}
              className={cn(
                'font-mono text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-colors text-white',
                isOpen === true && 'bg-green-700 hover:bg-green-800 border-green-800',
                isOpen === false && 'bg-wca-red hover:bg-wca-red-dark border-wca-red-dark',
                isOpen === null &&
                  'bg-wca-gray-light text-wca-gray-mid border-wca-gray-light opacity-60',
                togglingOpen && 'opacity-60 cursor-wait',
              )}
              title="Toggle whether teachers can place new orders"
            >
              <span aria-hidden="true">{isOpen === true ? '●' : isOpen === false ? '○' : '—'}</span>
              {isOpen === true ? 'Shop open' : isOpen === false ? 'Shop closed' : 'Loading'}
            </button>
            <button
              type="button"
              onClick={handleToggleSound}
              className="text-wca-gray-mid hover:text-wca-black font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 px-3 py-2 border border-wca-gray-light rounded-full bg-wca-surface"
              aria-pressed={soundOn}
            >
              {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {soundOn ? 'Sound on' : 'Sound off'}
            </button>
          </div>
        </div>

        <StatsBar orders={orders} />
      </div>

      {error !== null && (
        <p className="mx-6 mb-4 text-sm text-wca-red bg-wca-red-light rounded-lg p-3" role="alert">
          {error}
        </p>
      )}

      {isLoading ? (
        <p className="text-center text-wca-gray-mid font-mono text-sm py-20">Loading orders…</p>
      ) : (
        <KitchenBoard orders={visibleOrders} onAdvance={handleAdvance} onCancel={handleCancel} />
      )}
    </div>
  );
}
